import React, { FC, useState, useEffect } from 'react';
import { HoroscopeColor, HoroscopeIconType } from '../../Horoscopes/HoroscopesIcon/HoroscopesIcon-Constants';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@moment/hooks/useTheme.ts';
import './HoroscopesCard.scss';

interface HoroscopesCardProps {
  sign: HoroscopeIconType;
  date: string;
  text: string;
  onClick?: () => void;
}

export const HoroscopesCard: FC<HoroscopesCardProps> = ({ sign, date, text, onClick }) => {
  const [IconComponent, setIconComponent] = useState<React.FC<React.SVGProps<SVGSVGElement>> | null>(null);
  const { t } = useTranslation();
  const { theme } = useTheme();

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const signLower = sign.toLowerCase();
        const module = await import(`@/assets/horoscopes/${signLower}.svg?react`);
        setIconComponent(() => module.default);
      } catch (error) {
        console.error(`Failed to load icon for ${sign}:`, error);
      }
    };

    loadIcon();
  }, [sign]);

  const backgroundColor = HoroscopeColor[sign as keyof typeof HoroscopeColor];
  
  const translatedSign = t(sign.toString());
  const capitalizedSign = translatedSign.charAt(0).toUpperCase() + translatedSign.slice(1);

  // Add class for light theme to show drop shadow
  const cardClassName = `horoscopes-card-v2 ${theme === 'light' ? 'horoscopes-card-v2--light' : ''}`;

  return (
    <div
      className={cardClassName}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      data-testid="horoscopes-card-v2"
    >
      <div
        className="horoscopes-card-v2__header"
        style={{ backgroundColor }}
      >
        <div className="horoscopes-card-v2__illustration">
          {IconComponent && <IconComponent className="horoscopes-card-v2__icon" />}
        </div>
        <h3 className="horoscopes-card-v2__sign heading-md">{capitalizedSign}</h3>
        <p className="horoscopes-card-v2__date body-md">{t(date.toString())}</p>
      </div>
      <div className="horoscopes-card-v2__content">
        <p className="horoscopes-card-v2__text title-xs">{text}</p>
      </div>
    </div>
  );
};

export default HoroscopesCard;

